const PX_PER_MINUTE = 56 / 60; // 56px per hour
const pad = (n) => n.toString().padStart(2, "0");

// Date#toISOString renders in UTC, so using it on a Date built from local
// midnight shifts the day by one in any timezone east of Greenwich. Every
// YYYY-MM-DD string in this component is a local calendar date, so format it
// from the local getters instead.
const toLocalDateString = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toLocalTimeString = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export function activityDayCalendarData({ fetchUrl, initialDate, initialStart, initialEnd, adoptLabels = {} }) {
  const now = new Date();
  const start = new Date(now);
  start.setSeconds(0, 0);
  start.setMinutes(Math.ceil(now.getMinutes() / 5) * 5);
  const end = new Date(start.getTime() + 15 * 60 * 1000);
  const localDate = toLocalDateString(start);
  const localStart = toLocalTimeString(start);
  const localEnd = toLocalTimeString(end);
  return {
    fetchUrl,
    date: initialDate || localDate,
    startTime: initialStart || localStart,
    endTime: initialEnd || localEnd,
    draftLabel: "",
    existing: [],
    adoptedEventId: null,
    adoptLabels,
    pxPerMinute: PX_PER_MINUTE,
    nowMinutes: 0,
    _nowTimer: null,
    _fetchSeq: 0,

    init() {
      this.tickNow();
      this._nowTimer = setInterval(() => this.tickNow(), 60000);
      this.$watch("date", () => this.loadExisting());
      this.loadExisting();
      this.$nextTick(() => this.observeVisibility());
    },

    destroy() {
      if (this._nowTimer) clearInterval(this._nowTimer);
      if (this._visibilityObserver) this._visibilityObserver.disconnect();
    },

    observeVisibility() {
      const target = this.$refs.scroller;
      if (!target || typeof IntersectionObserver === "undefined") {
        this.scrollToNow();
        return;
      }
      this._visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.target.clientHeight > 0) {
            this.scrollToNow();
          }
        }
      });
      this._visibilityObserver.observe(target);
    },

    tickNow() {
      const d = new Date();
      this.nowMinutes = d.getHours() * 60 + d.getMinutes();
    },

    get isToday() {
      return this.date === toLocalDateString(new Date());
    },

    get headerLabel() {
      return new Date(`${this.date}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric"
      });
    },

    hours() {
      return Array.from({ length: 24 }, (_, i) => i);
    },

    minutesFromMidnight(timeStr) {
      const [h, m] = (timeStr || "0:0").split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    },

    blockStyle(startMinutes, durationMinutes) {
      const top = startMinutes * this.pxPerMinute;
      const height = Math.max(24, (durationMinutes || 30) * this.pxPerMinute);
      return `top: ${top}px; height: ${height}px;`;
    },

    draftStyle() {
      const start = this.minutesFromMidnight(this.startTime);
      const end = this.minutesFromMidnight(this.endTime);
      const duration = Math.max(15, end - start);
      return this.blockStyle(start, duration);
    },

    existingStyle(item) {
      const occurredAt = new Date(item.occurred_at);
      const start = occurredAt.getHours() * 60 + occurredAt.getMinutes();
      return this.blockStyle(start, item.duration_minutes);
    },

    existingClass(item) {
      if (item.kind === "calendar_event") {
        if (item.id === this.adoptedEventId) {
          return "bg-primary-100 text-primary-800 ring-1 ring-inset ring-primary-400 dark:bg-primary-900/40 dark:text-primary-200 dark:ring-primary-600";
        }
        const base =
          "bg-gray-300/60 text-gray-700 ring-1 ring-inset ring-gray-400/40 dark:bg-gray-700/60 dark:text-gray-200 dark:ring-gray-500/40";
        return this.isAdoptable(item)
          ? `${base} cursor-pointer hover:ring-primary-500 dark:hover:ring-primary-500`
          : base;
      }
      return "bg-primary-200/80 text-primary-900 ring-1 ring-inset ring-primary-300 dark:bg-primary-900/60 dark:text-primary-100 dark:ring-primary-700";
    },

    pickTimeAt(event) {
      if (event.target.closest("[data-existing-block]") || event.target.closest("[data-draft-block]")) {
        return;
      }
      const container = this.$refs.scroller;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const y = event.clientY - rect.top + container.scrollTop;
      const totalMinutes = Math.round(y / this.pxPerMinute / 15) * 15;
      const clamped = Math.max(0, Math.min(23 * 60 + 45, totalMinutes));
      const h = Math.floor(clamped / 60);
      const m = clamped % 60;
      const newStart = `${pad(h)}:${pad(m)}`;
      const endTotal = clamped + 30;
      const endH = Math.floor(endTotal / 60) % 24;
      const endM = endTotal % 60;
      const newEnd = `${pad(endH)}:${pad(endM)}`;
      this.startTime = newStart;
      this.endTime = newEnd;
      this.$dispatch("activity-time-picked", { start: newStart, end: newEnd });
    },

    nowLineStyle() {
      return `top: ${this.nowMinutes * this.pxPerMinute}px;`;
    },

    shiftDay(delta) {
      const d = new Date(`${this.date}T00:00:00`);
      d.setDate(d.getDate() + delta);
      this.date = toLocalDateString(d);
    },

    // An imported calendar event that isn't backed by an Activity yet can be
    // adopted: the modal takes over its title and time and links the two on save.
    isAdoptable(item) {
      return item.kind === "calendar_event" && item.adoptable === true;
    },

    adoptPayload(item) {
      const start = new Date(item.occurred_at);
      const end = new Date(start.getTime() + (item.duration_minutes || 30) * 60 * 1000);
      return {
        id: item.id,
        subject: item.subject,
        date: toLocalDateString(start),
        start: toLocalTimeString(start),
        end: toLocalTimeString(end)
      };
    },

    adoptEvent(item) {
      if (!this.isAdoptable(item)) return;
      const payload = this.adoptPayload(item);
      this.startTime = payload.start;
      this.endTime = payload.end;
      this.draftLabel = payload.subject;
      this.adoptedEventId = payload.id;
      this.$dispatch("calendar-event-adopt", payload);
    },

    releaseAdoption() {
      this.adoptedEventId = null;
    },

    adoptHint(item) {
      const key = item.id === this.adoptedEventId ? "hint_selected" : "hint";
      return this.adoptLabels[key] || "";
    },

    adoptAriaLabel(item) {
      const key = item.id === this.adoptedEventId ? "aria_selected" : "aria";
      return (this.adoptLabels[key] || "").replace("%{subject}", item.subject || "");
    },

    async loadExisting() {
      const seq = ++this._fetchSeq;
      try {
        const url = `${this.fetchUrl}?date=${this.date}`;
        const resp = await fetch(url, { headers: { Accept: "application/json" } });
        if (!resp.ok) return;
        const data = await resp.json();
        if (seq !== this._fetchSeq) return;
        this.existing = data;
      } catch {
        // ignore network errors; calendar simply won't update
      }
    },

    scrollToDraft() {
      const container = this.$refs.scroller;
      if (!container) return;
      const start = this.minutesFromMidnight(this.startTime || "07:00");
      container.scrollTop = Math.max(0, start * this.pxPerMinute - 60);
    },

    scrollToNow() {
      const container = this.$refs.scroller;
      if (!container) return;
      const center = this.nowMinutes * this.pxPerMinute - container.clientHeight / 2;
      container.scrollTop = Math.max(0, center);
    }
  };
}

export default function registerActivityDayCalendar(Alpine) {
  Alpine.data("activityDayCalendar", (options) => activityDayCalendarData(options));
}
